import { useCallback } from 'react';
import { nanoid } from 'nanoid';
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  insertedTemplateMetricsState,
  InsertedTemplateDataSource,
  reportTemplateState,
  templateInsertRequestState,
} from 'domains/reporter/Reporter/state';
import type { TemplateInsertRequestState } from 'domains/reporter/Reporter/state';
import {
  type TemplateInsertionContext,
  type TemplateInsertionStart,
  templateInsertionMetrics,
} from 'domains/reporter/RichTextEditor/analytics/performance';

type SetTemplateInsertionParams = {
  insertionContext?: TemplateInsertionContext | TemplateInsertionStart;
  templateId: string;
  source: InsertedTemplateDataSource;
  worklistItemSmid: string;
};

export const useTemplateInsertionState = () => {
  const setInsertedTemplateMetricsState = useSetRecoilState(insertedTemplateMetricsState);
  const setTemplateInsertRequestState = useSetRecoilState(templateInsertRequestState);
  const residentTemplate = useRecoilValue(reportTemplateState);

  // Start is written at the last user action before the insertion becomes inevitable, so
  // the measured latency never includes human dwell time.
  const startTemplateInsertion = useCallback(
    (source: InsertedTemplateDataSource): TemplateInsertionStart => {
      const startedInsertion = { insertionId: nanoid(), source };
      templateInsertionMetrics.start(startedInsertion);
      return startedInsertion;
    },
    []
  );

  const setTemplateInsertion = useRecoilCallback(
    ({ snapshot }) =>
      ({ insertionContext, templateId, source, worklistItemSmid }: SetTemplateInsertionParams) => {
        const startedInsertion = insertionContext ?? startTemplateInsertion(source);
        const wasTemplateResident =
          'wasTemplateResident' in startedInsertion &&
          typeof startedInsertion.wasTemplateResident === 'boolean'
            ? startedInsertion.wasTemplateResident
            : residentTemplate?.id === templateId;
        // The template is known by this point, so the request is now complete enough to
        // tag the staged measures.
        const insertRequest: TemplateInsertRequestState = {
          insertionId: startedInsertion.insertionId,
          templateId,
          source,
          wasTemplateResident,
          worklistItemSmid,
        };
        // Publishing supersedes any request still waiting to be picked up, and nothing
        // downstream will ever see that insertionId again to close out its marks.
        const supersededRequest = snapshot.getLoadable(templateInsertRequestState).valueMaybe();
        if (
          supersededRequest != null &&
          supersededRequest.insertionId !== insertRequest.insertionId
        ) {
          templateInsertionMetrics.cancel(supersededRequest);
        }
        // Reusing the currently unfurled template is the insertion's cache-hit path. The
        // network-only refresh still runs, but it does not gate what the user sees.
        if (wasTemplateResident) templateInsertionMetrics.markFetchData(insertRequest);
        setInsertedTemplateMetricsState({
          templateId,
          source,
          hasConfirmationDialogTriggered: false,
        });
        setTemplateInsertRequestState(insertRequest);

        return insertRequest;
      },
    [
      residentTemplate?.id,
      setInsertedTemplateMetricsState,
      setTemplateInsertRequestState,
      startTemplateInsertion,
    ]
  );

  const markTemplateInsertionConfirmationDialogTriggered = useCallback(() => {
    setInsertedTemplateMetricsState((prev) => {
      if (prev == null) {
        return prev;
      }
      return { ...prev, hasConfirmationDialogTriggered: true };
    });
  }, [setInsertedTemplateMetricsState]);

  const clearTemplateInsertRequestIfMatches = useCallback(
    (insertionId: string) => {
      setTemplateInsertRequestState((previousState) =>
        previousState?.insertionId === insertionId ? null : previousState
      );
    },
    [setTemplateInsertRequestState]
  );

  return {
    setTemplateInsertion,
    startTemplateInsertion,
    markTemplateInsertionConfirmationDialogTriggered,
    clearTemplateInsertRequestIfMatches,
  };
};
